import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  AppBar,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useLocation } from 'react-router-dom';
import { useDashboard } from '../../contexts/DashboardContext';
import { useDashboardData } from '../../hooks/useDashboardData';
import {
  detectLanguageFromPathname,
  getActionLabel,
  getLocationLabel,
  stripLanguagePrefix,
  translate,
} from '../../i18n';
import { buildAiConsultPrompt, detectDashboardUiMode } from '../../utils/promptGenerator';
import { generateDecisionSummary } from '../../utils/decisionSummaryGenerator';
import theme from '../../theme/theme';

function getBarColor(pathname) {
  const normalizedPath = stripLanguagePrefix(pathname);
  const isPurpose = normalizedPath === '/' || normalizedPath === '/purpose';
  const isFunction = normalizedPath === '/function';
  if (isPurpose) return '#4A5568';
  if (isFunction) return '#553C9A';
  return theme.palette.primary.main;
}

function AIPromptModal({ open, onClose }) {
  const routerLocation = useLocation();
  const lang = detectLanguageFromPathname(routerLocation.pathname);
  const t = (key, params) => translate(lang, `aiPrompt.${key}`, params);
  const isMobile = useMediaQuery('(max-width:768px)');
  const barColor = getBarColor(routerLocation.pathname);

  const {
    location: locationKey,
    action,
    year,
    month,
  } = useDashboard();

  const {
    calendarData,
    data,
    eventData,
  } = useDashboardData();

  const purposeLabel = action ? getActionLabel(action, lang).label : '';
  const uiMode = detectDashboardUiMode(routerLocation.pathname);
  const locationLabel = locationKey ? getLocationLabel(locationKey, lang) : '';
  const periodLabel = lang === 'en'
    ? `${month}/${year}`
    : `${year}年${month}月`;

  const decisionSummary = useMemo(
    () => generateDecisionSummary({
      action,
      calendarData,
      data,
      eventData,
      year,
      month,
      lang,
    }),
    [action, calendarData, data, eventData, year, month, lang],
  );

  const generatedPrompt = useMemo(() => {
    if (decisionSummary.unsupportedReason) return '';
    return buildAiConsultPrompt({
      lang,
      purposeLabel,
      locationLabel,
      periodLabel,
      locationKey: locationKey || '',
      monthNum: month,
      action,
      eventData: eventData || [],
      textualSummary: decisionSummary.textualSummary,
      dataTables: decisionSummary.dataTables || [],
      userNotes: '',
      uiMode,
      hasPrediction: Boolean(decisionSummary.hasPrediction),
    });
  }, [
    lang,
    purposeLabel,
    uiMode,
    locationLabel,
    periodLabel,
    locationKey,
    month,
    action,
    eventData,
    decisionSummary,
  ]);

  const [promptText, setPromptText] = useState('');
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackError, setSnackError] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPromptText(generatedPrompt);
  }, [open, generatedPrompt]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(promptText);
      setSnackError(false);
      setSnackOpen(true);
    } catch {
      setSnackError(true);
      setSnackOpen(true);
    }
  };

  const unsupported = Boolean(decisionSummary.unsupportedReason);

  return (
    <>
      <Dialog
        fullScreen={isMobile}
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        slotProps={{
          paper: {
            sx: {
              maxHeight: isMobile ? '100%' : 'min(92vh, 900px)',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        <AppBar position="sticky" elevation={0} sx={{ backgroundColor: barColor }}>
          <Toolbar variant="dense" sx={{ minHeight: 48 }}>
            <Typography sx={{ flex: 1 }} variant="subtitle1" component="div" fontWeight={600}>
              {t('modalTitle')}
            </Typography>
            <IconButton edge="end" color="inherit" onClick={onClose} aria-label={translate(lang, 'header.closeModal')}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <DialogContent
          dividers
          sx={{
            p: isMobile ? 1.5 : 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            overflow: 'auto',
            flex: 1,
          }}
        >
          {unsupported ? (
            <Typography color="text.secondary">{decisionSummary.unsupportedReason}</Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                {t('modalIntro')}
              </Typography>

              <Paper
                variant="outlined"
                sx={{
                  p: isMobile ? 1.25 : 1.5,
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                }}
              >
                <Stack
                  direction={isMobile ? 'column' : 'row'}
                  spacing={1}
                  alignItems={isMobile ? 'stretch' : 'center'}
                  justifyContent="space-between"
                  sx={{ mb: 1.25 }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, flex: 1 }}>
                    {t('copyAreaExplain')}
                  </Typography>
                  <Button
                    variant="contained"
                    size="medium"
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopy}
                    disabled={!promptText}
                    sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
                  >
                    {t('copy')}
                  </Button>
                </Stack>

                <TextField
                  value={promptText}
                  onChange={(e) => setPromptText(e.target.value)}
                  fullWidth
                  multiline
                  minRows={isMobile ? 6 : 7}
                  hiddenLabel
                  InputProps={{
                    'aria-label': t('promptAriaLabel'),
                    sx: {
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      fontSize: '0.8rem',
                      alignItems: 'flex-start',
                      bgcolor: 'background.paper',
                      '& textarea': {
                        maxHeight: isMobile ? 240 : 320,
                        overflow: 'auto !important',
                      },
                    },
                  }}
                />
              </Paper>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackError ? 'error' : 'success'} onClose={() => setSnackOpen(false)}>
          {snackError ? t('copyFailed') : t('copied')}
        </Alert>
      </Snackbar>
    </>
  );
}

AIPromptModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AIPromptModal;
